import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { DynamicObjectInterface } from '../../../../sdk/type';
import {
  Input,
  InputTypes,
  MLPVersion,
  Theme,
  ButtonStyles,
  Button,
  ButtonSize,
  RadioButton,
  DropDown,
  Modal,
  ButtonTypes,
  TextAlign,
  Checkbox,
  InputValidations,
  SearchComponent,
} from '../../../../ui/components';
import { useAdminToast } from '../../../../ui/components/AdminToast/AdminToast';
import { useAPICall } from '../../../../hooks';
import { CreateTeamData } from '../teamsListConfig';
import { DropDownData } from '../../../../ui/components/Form/DropDown/types/propsType';
import { isValidEmail } from '../../../../ui/utils/common';
import TeamService from '../../../../sdk/services/teamService';
import CampaignService from '../../../../sdk/services/campaignService';
import EnrollmentService from '../../../../sdk/services/enrollmentService';
import styles from './index.module.scss';

const initTeamForm: CreateTeamData = {
  teamId: null,
  teamName: '',
  campaignId: '',
  coachId: '',
  teamLeader: '',
  teamStartDate: '',
  teamEndDate: '',
  status: '',
  remark: '',
  participant: [],
  checkbox: '',
  isIndividualTeam: null,
  coachDetail: {},
  campaignName: '',
};

export const AdminTeamCreatePage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { adminToast } = useAdminToast();
  const id = searchParams.get('');

  const [masterList, setMasterList] = useState<{
    coachList: DynamicObjectInterface;
    campaignList: DynamicObjectInterface;
  }>();
  const [filteredCoachList, setFilteredCoachList] = useState(masterList?.coachList);
  const [teamData, setTeamData] = useState<CreateTeamData>(initTeamForm);
  const [teamSize, setTeamSize] = useState(0);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [showNotification, setShowNotification] = useState(false);
  const [clickedFields, setClickedFields] = useState({
    campaignId: false,
    teamName: false,
    teamLeader: false,
    participant: [],
  });
  const [invalid, setInvalid] = useState(true);
  const [emailInvalid, setEmailInvalid] = useState<{
    teamLeader: boolean;
    [key: string]: boolean | undefined;
  }>({ teamLeader: false });

  const apiCall = useAPICall();

  const updateCoachEnrollmentList = async (campaignId: string) => {
    const res = await apiCall(EnrollmentService.getEnrollmentList, 'COACH', campaignId);
    const coachEnrollmentList = res?.data?.data?.map((d: DynamicObjectInterface) => ({
      ...d,
      coachDetail: d,
      id: d.coachId,
      title: `${d.coachId} - ${d.name}`,
    }));
    setMasterList((i: DynamicObjectInterface) => {
      i.coachList = coachEnrollmentList;
      return { ...i };
    });
    setFilteredCoachList(coachEnrollmentList);
  };

  const handleInputChange = async (name: string, value: DynamicObjectInterface) => {
    if (name.startsWith('participant_')) {
      setTeamData((prevTeamData: DynamicObjectInterface) => ({
        ...prevTeamData!,
        participant: {
          ...prevTeamData.participant,
          [name]: value,
        },
      }));
      setClickedFields(prevClickedFields => ({
        ...prevClickedFields,
        participant: {
          ...prevClickedFields.participant,
          [name]: value === '',
        },
      }));
      setEmailInvalid((prevData: DynamicObjectInterface) => ({
        ...prevData!,
        [name]: value !== '' && !isValidEmail(value as string),
      }));
    } else {
      if (name === 'checkbox' && teamData?.checkbox) {
        value = '';
      }

      if (name === 'campaignId') {
        await updateCoachEnrollmentList(value);
        setEmailInvalid({ teamLeader: false });
        setTeamData((prevTeamData: DynamicObjectInterface) => ({
          ...prevTeamData!,
          teamLeader: '',
          participant: {},
        }));

        let campaignRecId = masterList?.campaignList?.find((c: DynamicObjectInterface) => c.value === value)?.recId;

        let result = campaignRecId && (await apiCall(CampaignService.getCampaignDetail, `${campaignRecId}`));
        setTeamSize(result?.data?.data?.activities?.participant?.teamSize);
      }

      if (name === 'teamLeader') {
        setEmailInvalid((prevData: DynamicObjectInterface) => ({
          ...prevData!,
          [name]: !isValidEmail(value as string),
        }));
      }

      if (name === 'isIndividualTeam') {
        teamData.isIndividualTeam = value;
        if (value === 1) {
          teamData.teamName = 'Individual';
          setClickedFields({
            campaignId: false,
            teamName: false,
            teamLeader: false,
            participant: [],
          });
          setEmailInvalid({ teamLeader: false });
        } else {
          teamData.teamName = '';
        }
      }

      setTeamData(prevTeamData => ({
        ...prevTeamData!,
        [name]: value,
      }));

      setClickedFields(prevClickedFields => ({
        ...prevClickedFields,
        [name]: value === '',
      }));
    }
  };

  const onSave = async () => {
    const payload = {
      campaignId: teamData?.campaignId,
      campaignName: teamData?.campaignName,
      coachDetail: teamData.coachDetail,
      teamName: teamData?.teamName,
      coachId: teamData?.coachId,
      isIndividualTeam: teamData.isIndividualTeam || 0,
      memberEmails: [
        ...Object.values(teamData?.participant ?? {}).map(email => ({ email, isTeamLead: false })),
        { email: teamData?.teamLeader, isTeamLead: true },
      ],
      loginId: '',
      description: teamData?.remark,
      startDate: new Date().toISOString().slice(0, -1),
    };

    let result = await apiCall(TeamService.createTeam, payload);
    if (!result?.data?.result) {
      if (result.status !== 500) {
        adminToast({
          message: result?.data?.message,
          theme: Theme.ERROR,
        });
      }
    } else {
      setShowConfirmation(false);
      setShowNotification(true);
    }
  };

  const searchCoach = (input: string) => {
    if (!input) {
      setFilteredCoachList(masterList?.coachList);
    } else {
      const filteredUsers = masterList?.coachList?.filter((coach: DynamicObjectInterface) => {
        return coach.title.toLowerCase().includes(input.toLowerCase());
      });
      setFilteredCoachList(filteredUsers);
    }
  };

  const teamDetailsBtnConfig = [
    {
      title: t('admin.teamCreate.cancel'),
      buttonStyle: ButtonStyles.SOLID,
      onClick: () => navigate(`/admin/manage-team/list`),
    },
    {
      title: t('admin.teamCreate.continue'),
      buttonStyle: ButtonStyles.SOLID,
      disabled: invalid,
      onClick: () => {
        setShowConfirmation(true);
      },
    },
  ];

  const confirmationPrompt = (
    <>
      {showConfirmation && (
        <Modal
          primaryButtonLabel={''}
          headline={'Confirmation'}
          closeIcon={false}
          containerWidth={'max-content'}
          textAlign={TextAlign.LEFT}
          primaryButton={
            <Button
              title={t('admin.teams.confirm')}
              type={ButtonTypes.BUTTON}
              onClick={() => {
                onSave();
              }}
              size={ButtonSize.SMALL}
              buttonStyle={ButtonStyles.SOLID}
              data-testid="test-add-button-popup"
              disabled={!teamData?.checkbox}
            />
          }
          secondaryButton={
            <Button
              title={t('admin.teams.back')}
              type={ButtonTypes.BUTTON}
              onClick={() => {
                setShowConfirmation(false);
              }}
              size={ButtonSize.SMALL}
              buttonStyle={ButtonStyles.OUTLINE}
              data-testid="test-cancel-button-popup-delete-modal"
            />
          }
        >
          <>
            <div
              style={{
                fontSize: '16px',
                fontFamily: 'GT-Walsheim-Pro-Bold, sans-serif',
                lineHeight: '24px',
                margin: '10px 0',
              }}
            >
              {t('admin.teamCreate.confirmation')}
            </div>
            <div style={{ display: 'flex', margin: '32px 0' }}>
              <Checkbox
                key={'checkbox_confirmation'}
                className={styles['checkbox']}
                checked={teamData.checkbox === 'on'}
                theme={Theme.BLACK}
                text={
                  <p
                    id="agree-checkbox"
                    dangerouslySetInnerHTML={{
                      __html: t('admin.teamCreate.termsLine1').replace(
                        '{terms-conditions}',
                        `<a href="/terms-and-conditions?cid=${teamData?.campaignId}" target="_blank">${t('securityPrivacy.terms-condition')}</a>`,
                      ),
                    }}
                  />
                }
                onChange={e => handleInputChange('checkbox', e.target.value)}
              />

              {/* {t('admin.teamCreate.termsLine1')} */}
              <br />
              {/* {t('admin.teamCreate.termsLine2')} */}
            </div>
          </>
        </Modal>
      )}
    </>
  );

  const notificationPrompt = (
    <>
      {showNotification && (
        <Modal
          primaryButtonLabel={''}
          containerWidth={'450px'}
          headline={t('admin.teamCreate.teamSetup')}
          closeIcon={false}
          textAlign={TextAlign.CENTER}
          primaryButton={
            <Button
              title={t('admin.teamCreate.backToDashboard')}
              type={ButtonTypes.BUTTON}
              onClick={() => {
                setShowNotification(false);
                navigate(`/admin/manage-team/list`);
              }}
              size={ButtonSize.SMALL}
              buttonStyle={ButtonStyles.SOLID}
              data-testid="test-add-button-popup"
            />
          }
        >
          <div
            style={{
              fontSize: '16px',
              lineHeight: '24px',
              margin: '10px 0',
            }}
          >
            {t('admin.teamCreate.sentEmail')}
          </div>
        </Modal>
      )}
    </>
  );

  const participantInput = Array.from({ length: teamSize - 1 }, (_, index) => {
    const plusOne = index + 1;
    return (
      <div>
        <p className={styles['input-label']}>
          {t('admin.teamCreate.participant')} {plusOne + 1}
        </p>
        <Input
          key={`${teamData?.campaignId}_participant_${index}`}
          type={InputTypes.TEXT}
          name={`participant_${index + 1}`}
          mlpVersion={MLPVersion.ONE}
          onChange={e => {
            handleInputChange(`participant_${index}`, e.target.value);
          }}
          status={emailInvalid?.[`participant_${index}`] ? InputValidations.INVALID : InputValidations.DEFAULT}
          message={emailInvalid?.[`participant_${index}`] ? `${t('admin.teamCreate.invalidEmailFormat')}` : ''}
        />
      </div>
    );
  });

  const validateInputs = () => {
    const { teamName, campaignId, participant } = teamData;
    const requiredFields = [teamName, campaignId, participant];
    const hasEmptyFields = requiredFields.some(field => field === '');

    if (hasEmptyFields || emailInvalid.teamLeader || !teamData.teamLeader) {
      setInvalid(true);
    } else {
      setInvalid(false);
    }
  };

  useEffect(() => {
    (async () => {
      let apiList = [apiCall(CampaignService.getCampaigns)];
      let res = await Promise.all(apiList);

      let masterList = {
        coachList: [],
        campaignList: (
          res?.[0]?.data?.data.filter((i: DynamicObjectInterface) => i.status !== 'DELETED' && i.status !== 'CLOSED') ||
          []
        ).map((d: DynamicObjectInterface) => ({
          ...d,
          campaignName: d.campaignName,
          value: d.campaignId,
          title: `${d.campaignId} - ${d.campaignName}`,
        })),
      };
      setMasterList(masterList);
      await updateCoachEnrollmentList(masterList?.campaignList?.[0]?.value);
      setTeamData(prevTeamData => ({
        ...prevTeamData,
        campaignId: masterList?.campaignList[0].value,
        campaignName: masterList?.campaignList[0].campaignName,
      }));
      let campaignRecId = masterList?.campaignList?.[0]?.recId;
      let result = await apiCall(CampaignService.getCampaignDetail, `${campaignRecId}`);
      setTeamSize(result?.data?.data?.activities?.participant?.teamSize);
    })();
  }, []);

  useEffect(() => {
    validateInputs();
  }, [teamData]);

  return (
    <div className={styles['edit-team']}>
      <div className={styles['card']}>
        <div className={styles['card-header']}>
          <h2>{t('admin.teamCreate.title')}</h2>
          <div className={styles['actions-container']}>
            {teamDetailsBtnConfig.map(btn => (
              <Button key={btn.title} size={ButtonSize.SMALL} {...btn} />
            ))}
          </div>
        </div>
        <div className={styles['create-team-form-container']}>
          <DropDown
            name="campaignId"
            dropdownLabel={`${t('admin.teams.campaign')}*`}
            disabledFilter={false}
            mlpVersion={MLPVersion.ONE}
            value={
              masterList?.campaignList?.find((c: DynamicObjectInterface) => c.value === teamData?.campaignId)?.title
            }
            data={masterList?.campaignList}
            configData={{
              label: 'title',
              onSelect: (selectedOption: DropDownData) => {
                handleInputChange('campaignId', selectedOption.value);
                handleInputChange('campaignName', selectedOption.campaignName);
              },
            }}
            readOnly={false}
          />
          <SearchComponent
            status={InputValidations.DEFAULT}
            label={t('admin.teams.coachId')}
            placeholder={t('admin.teams.searchCoach')}
            initialSearchText={
              masterList?.coachList?.find((c: DynamicObjectInterface) => c.id === teamData?.coachId)?.title
            }
            labelChoice={'title'}
            dropdownOptions={filteredCoachList}
            onSearch={searchCoach}
            onClear={() => {
              searchCoach('');
              handleInputChange('coachId', '');
              handleInputChange('coachDetail', '');
            }}
            onSelect={e => {
              handleInputChange('coachId', e.id);
              handleInputChange('coachDetail', (e as any).coachDetail);
            }}
          />
        </div>
        <div className={styles['create-team-form-container']}>
          <Input
            key={id}
            type={InputTypes.TEXT}
            name={'teamName'}
            label={`${t('admin.teams.teamName')}`}
            value={teamData?.teamName}
            required
            mlpVersion={MLPVersion.ONE}
            disabled={teamData?.isIndividualTeam === 1}
            onChange={e => {
              handleInputChange('teamName', e.target.value);
            }}
            status={clickedFields.teamName ? InputValidations.INVALID : InputValidations.DEFAULT}
            message={clickedFields.teamName ? `${t('admin.teamCreate.teamNameRequired')}` : ''}
          />
          <div className={styles['input-status']}>
            <span className={styles['input-label']}>{t('admin.teams.isIndividualTeam')}</span>
            <div className={styles['input-radio']}>
              <RadioButton
                label="Team"
                checked={teamData?.isIndividualTeam !== 1}
                value={0}
                onChange={e => handleInputChange('isIndividualTeam', Number(e.target.value))}
              />
              <RadioButton
                label="Individual"
                checked={teamData?.isIndividualTeam === 1}
                value={1}
                onChange={e => handleInputChange('isIndividualTeam', Number(e.target.value))}
              />
            </div>
          </div>
        </div>
        <div className={styles['create-team-form-container']}>
          <Input
            key={id}
            type={InputTypes.TEXT}
            name={'remark'}
            label={`${t('admin.teams.remark')}`}
            value={teamData?.remark}
            mlpVersion={MLPVersion.ONE}
            onChange={e => {
              handleInputChange('remark', e.target.value);
            }}
          />
        </div>

        <div className={styles['card-send-email-invitation']}>
          <div className={styles['card-send-email-invitation-title']}>{`${t(
            'admin.teamCreate.sendEmailInvitation',
          )}`}</div>
          <div className={styles['card-send-email-invitation-desc']}>{`${t(
            'admin.teamCreate.invitationDescription',
          )}`}</div>
          <div className={styles['card-send-email-invitation-input-container']}>
            <div className={styles['card-send-email-invitation-input-container-item']}>
              <p className={styles['input-label']}>
                {t('admin.teamCreate.teamLeader')} <span style={{ color: 'red' }}> *</span>
              </p>
              <div className={styles['input-wrapper']}>
                <Input
                  key={id}
                  type={InputTypes.TEXT}
                  name={'teamLeader'}
                  // label={`${t('admin.teamCreate.teamLeader')}*`}
                  value={teamData?.teamLeader}
                  mlpVersion={MLPVersion.ONE}
                  onChange={e => {
                    handleInputChange('teamLeader', e.target.value);
                  }}
                  status={
                    clickedFields.teamLeader || emailInvalid?.['teamLeader']
                      ? InputValidations.INVALID
                      : InputValidations.DEFAULT
                  }
                  message={
                    clickedFields.teamLeader
                      ? `${t('admin.teamCreate.emailRequired')}`
                      : emailInvalid?.['teamLeader']
                        ? `${t('admin.teamCreate.invalidEmailFormat')}`
                        : ''
                  }
                />
                {/* <div className={styles['input-message']}>*Only ABOs can be team leaders.</div> */}
              </div>
            </div>
            {teamData.isIndividualTeam !== 1 && participantInput}
          </div>
        </div>
        <div className={styles['input-message']}>
          *All Participants must have a valid Pack Order Number that was purchased within 45 days before their
          registration date.
        </div>
      </div>
      {confirmationPrompt}
      {notificationPrompt}
    </div>
  );
};
